import { c as _c } from "react/compiler-runtime";
import { IcoChecklist16, IcoCode16, IcoMinusBig16, IcoOrderedList16, IcoQuoteLeft16, IcoTable16, IcoText16, IcoUnorderedList16 } from '@onefootprint/icons';
import { type Editor, Extension, type Range } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import { ReactRenderer } from '@tiptap/react';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import type { ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import tippy, { type Instance } from 'tippy.js';
export type SlashCommandItem = {
  title: string;
  description: string;
  icon: ReactNode;
  /** Items with no section lead the menu, unlabelled. */
  section?: string;
  command: (props: {
    editor: Editor;
    range: Range;
  }) => void;
};
type CommandItem = SlashCommandItem;
const BASIC_BLOCKS = 'Basic blocks';
const BUILT_INS: Omit<CommandItem, 'section'>[] = [{
  title: 'Text',
  description: 'Plain text paragraph',
  icon: <IcoText16 />,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).setParagraph().run();
  }
}, {
  title: 'Heading 1',
  description: 'Large section heading',
  icon: <span className="slash-menu-text-icon">H1</span>,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).setHeading({
      level: 1
    }).run();
  }
}, {
  title: 'Heading 2',
  description: 'Medium section heading',
  icon: <span className="slash-menu-text-icon">H2</span>,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).setHeading({
      level: 2
    }).run();
  }
}, {
  title: 'Heading 3',
  description: 'Small section heading',
  icon: <span className="slash-menu-text-icon">H3</span>,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).setHeading({
      level: 3
    }).run();
  }
}, {
  title: 'Bulleted list',
  description: 'Unordered list',
  icon: <IcoUnorderedList16 />,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).toggleBulletList().run();
  }
}, {
  title: 'Numbered list',
  description: 'Ordered list',
  icon: <IcoOrderedList16 />,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).toggleOrderedList().run();
  }
}, {
  title: 'To-do list',
  description: 'Checklist with checkboxes',
  icon: <IcoChecklist16 />,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).toggleTaskList().run();
  }
}, {
  title: 'Quote',
  description: 'Blockquote',
  icon: <IcoQuoteLeft16 />,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).setBlockquote().run();
  }
}, {
  title: 'Divider',
  description: 'Horizontal line separator',
  icon: <IcoMinusBig16 />,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).setHorizontalRule().run();
  }
}, {
  title: 'Code block',
  description: 'Code snippet',
  icon: <IcoCode16 />,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).setCodeBlock().run();
  }
}, {
  title: 'Table',
  description: 'Insert a table',
  icon: <IcoTable16 />,
  command: ({
    editor,
    range
  }) => {
    editor.chain().focus().deleteRange(range).insertTable({
      rows: 3,
      cols: 3,
      withHeaderRow: true
    }).run();
  }
}];
const COMMANDS: CommandItem[] = BUILT_INS.map(item => ({
  ...item,
  section: BASIC_BLOCKS
}));
type CommandListHandle = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

/** Entries keep the flat item index, which is what keyboard nav selects by. */
type CommandGroup = {
  section?: string;
  entries: {
    item: CommandItem;
    index: number;
  }[];
};
const groupBySection = (items: CommandItem[]): CommandGroup[] => items.reduce<CommandGroup[]>((groups, item, index) => {
  const open = groups.at(-1);
  if (open && open.section === item.section) open.entries.push({
    item,
    index
  });else groups.push({
    section: item.section,
    entries: [{
      item,
      index
    }]
  });
  return groups;
}, []);
const CommandList = forwardRef<CommandListHandle, {
  items: CommandItem[];
  command: (item: CommandItem) => void;
}>((t0, ref) => {
  const $ = _c(23);
  const {
    items,
    command
  } = t0;
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef(null);
  const isKeyboardNav = useRef(false);
  let t1;
  if ($[0] !== command || $[1] !== items) {
    t1 = index => {
      const item = items[index];
      if (item) {
        command(item);
      }
    };
    $[0] = command;
    $[1] = items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selectItem = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setSelectedIndex(-1);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== items) {
    t3 = [items];
    $[4] = items;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== selectedIndex) {
    t4 = () => {
      if (!isKeyboardNav.current) {
        return;
      }
      isKeyboardNav.current = false;
      const el = containerRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
      el?.scrollIntoView({
        block: "nearest"
      });
    };
    t5 = [selectedIndex];
    $[6] = selectedIndex;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== items.length || $[10] !== selectItem || $[11] !== selectedIndex) {
    t6 = () => ({
      onKeyDown: event => {
        if (event.key === "ArrowUp") {
          isKeyboardNav.current = true;
          setSelectedIndex(prev => prev <= 0 ? items.length - 1 : prev - 1);
          return true;
        }
        if (event.key === "ArrowDown") {
          isKeyboardNav.current = true;
          setSelectedIndex(prev_0 => prev_0 < 0 ? 0 : (prev_0 + 1) % items.length);
          return true;
        }
        if (event.key === "Enter") {
          if (selectedIndex >= 0) {
            selectItem(selectedIndex);
          }
          return true;
        }
        return false;
      }
    });
    $[9] = items.length;
    $[10] = selectItem;
    $[11] = selectedIndex;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useImperativeHandle(ref, t6);
  if (items.length === 0) {
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="slash-menu"><div className="slash-menu-empty">No results</div></div>;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  let t7;
  if ($[14] !== items || $[15] !== selectItem || $[16] !== selectedIndex) {
    let t8;
    if ($[18] !== selectItem || $[19] !== selectedIndex) {
      t8 = group => <div key={group.section ?? ""} className="slash-menu-group">{group.section && <div className="slash-menu-section">{group.section}</div>}{group.entries.map(t9 => {
          const {
            item: item_0,
            index: index_0
          } = t9;
          return <button key={item_0.title} type="button" data-index={index_0} className={`slash-menu-item ${index_0 === selectedIndex ? "is-selected" : ""}`} onClick={() => selectItem(index_0)} onMouseEnter={() => setSelectedIndex(index_0)}><span className="slash-menu-icon">{item_0.icon}</span><div className="slash-menu-text"><span className="slash-menu-title">{item_0.title}</span><span className="slash-menu-description">{item_0.description}</span></div></button>;
        })}</div>;
      $[18] = selectItem;
      $[19] = selectedIndex;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    t7 = groupBySection(items).map(t8);
    $[14] = items;
    $[15] = selectItem;
    $[16] = selectedIndex;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[21] !== t7) {
    t8 = <div ref={containerRef} className="slash-menu">{t7}</div>;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
});
const renderItems: SuggestionOptions['render'] = () => {
  let component: ReactRenderer;
  let popup: Instance[];
  let releasePage: (() => void) | null = null;

  // The menu is anchored to a caret, so a page that moves under it pulls the two apart.
  // Wheel/touch are cancelled unless the gesture is over the menu (its own list still
  // scrolls). Capture-phase scroll is the backstop for programmatic scrollIntoView —
  // Popper only watches body's scroll parents, not an overflow-y-auto editor column.
  const holdPageStill = () => {
    const isOverMenu = (target: EventTarget | null) => target instanceof Node && !!popup?.[0]?.popper?.contains(target);
    const blockScroll = (event: Event) => {
      if (!isOverMenu(event.target)) event.preventDefault();
    };
    const reposition = () => popup?.[0]?.popperInstance?.update();
    document.addEventListener('wheel', blockScroll, {
      capture: true,
      passive: false
    });
    document.addEventListener('touchmove', blockScroll, {
      capture: true,
      passive: false
    });
    document.addEventListener('scroll', reposition, true);
    releasePage = () => {
      document.removeEventListener('wheel', blockScroll, true);
      document.removeEventListener('touchmove', blockScroll, true);
      document.removeEventListener('scroll', reposition, true);
    };
  };
  return {
    onStart: props => {
      component = new ReactRenderer(CommandList, {
        props,
        editor: props.editor
      });
      if (!props.clientRect) {
        return;
      }
      popup = tippy('body', {
        getReferenceClientRect: props.clientRect as () => DOMRect,
        appendTo: () => document.body,
        content: component.element,
        showOnCreate: true,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start'
      });
      holdPageStill();
    },
    onUpdate: props => {
      component?.updateProps(props);
      if (!props.clientRect) {
        return;
      }
      popup?.[0].setProps({
        getReferenceClientRect: props.clientRect as () => DOMRect
      });
    },
    onKeyDown: props => {
      if (props.event.key === 'Escape') {
        popup?.[0].hide();
        return true;
      }
      return (component?.ref as CommandListHandle | null)?.onKeyDown(props.event) ?? false;
    },
    onExit: () => {
      releasePage?.();
      releasePage = null;
      popup?.[0].destroy();
      component?.destroy();
    }
  };
};
export const slashCommandPluginKey = new PluginKey('slashCommand');
export const SlashCommand = Extension.create<{
  suggestion: Partial<SuggestionOptions>;
  getExtraCommands: () => SlashCommandItem[];
}>({
  name: 'slashCommand',
  addOptions() {
    return {
      getExtraCommands: () => [],
      suggestion: {
        char: '/',
        pluginKey: slashCommandPluginKey,
        decorationTag: 'span',
        decorationClass: 'slash-command-pill',
        decorationContent: 'Type to search',
        items: ({
          query,
          editor
        }: {
          query: string;
          editor: Editor;
        }) => {
          const options = editor.extensionManager.extensions.find(item => item.name === 'slashCommand')?.options;
          const extra = (options?.getExtraCommands?.() ?? []) as SlashCommandItem[];
          const overridden = new Set(extra.map(item => item.title));
          const available = [...extra, ...COMMANDS.filter(item => !overridden.has(item.title))];
          return available.filter(item => item.title.toLowerCase().includes(query.toLowerCase()));
        },
        command: ({
          editor,
          range,
          props
        }: {
          editor: Editor;
          range: Range;
          props: CommandItem;
        }) => {
          props.command({
            editor,
            range
          });
        },
        render: renderItems
      } satisfies Partial<SuggestionOptions>
    };
  },
  addProseMirrorPlugins() {
    return [Suggestion({
      editor: this.editor,
      ...this.options.suggestion
    })];
  }
});